import { v4 as uuidv4 } from "uuid";
import { ReactElement } from "react";
import { SearchResultData } from "../../app/slices/SearchResultsSlice";
import { InteractionFactoryEntity } from "../../features/interaction_factory/InteractionFactory";
import ShowCustomQuizNodePreview from "../../features/sequence/node_previews/ShowCustomQuizNodePreview";
import { ValidationHelper } from "../../features/validation/ValidationHelper";
import { Actor } from "../Actor";
import Take from "../Take";
import Interaction, { InteractionType } from "./Interaction";

export interface IShowCustomQuiz extends Interaction {
  title: string; // should be a key
  showUIStars: boolean;
}

export const showCustomQuizInteraction = (): InteractionFactoryEntity => {
  const view: ReactElement = <></>;

  const create = (getKeys: (amount: number) => string[]): IShowCustomQuiz => {
    const keys = getKeys(1);
    return {
      id: uuidv4(),
      name: "Show Custom Quiz",
      type: InteractionType.ShowCustomQuiz,
      title: keys[0],
      showUIStars: false,
      properties: [],
    };
  };

  const migrate = (
    from: number,
    to: number,
    interaction: Interaction,
  ): IShowCustomQuiz => {
    return interaction as IShowCustomQuiz;
  };

  const copy = (
    interaction: Interaction,
    getKeys: (amount: number) => string[],
    replaceKey: (from: string, to: string) => void,
  ): IShowCustomQuiz => {
    const key = getKeys(1)[0];
    const original = interaction as IShowCustomQuiz;
    replaceKey(original.title, key);
    return {
      id: uuidv4(),
      name: "Show Custom Quiz",
      type: InteractionType.ShowCustomQuiz,
      title: key,
      showUIStars: original.showUIStars,
      properties: [],
    };
  };

  const validate = (
    interaction: Interaction,
    getValue: (key: string) => string,
    currentTake: Take,
    actorPresets: Actor[],
  ): string[] => {
    const converted = interaction as IShowCustomQuiz;
    const warnings: string[] = [];

    ValidationHelper.ValidateKey(converted.title, getValue, warnings, "Title");

    return warnings;
  };

  const validateErrors = (interaction: Interaction): string[] => {
    const converted = interaction as IShowCustomQuiz;
    const errors: string[] = [];

    if (!converted.title?.trim()) errors.push("Title is required");

    return errors;
  };

  const filter = (
    interaction: Interaction,
    takeName: string,
    getMatchingResults: (
      searchableStrings: string[],
      takeName: string,
      interaction: Interaction,
      filterType: string,
    ) => SearchResultData[],
    getValue: (key: string) => string,
  ): SearchResultData[] => {
    const converted = interaction as IShowCustomQuiz;
    const searchableStrings: string[] = [
      interaction.name,
      getValue(converted.title),
    ];

    return getMatchingResults(
      searchableStrings,
      takeName,
      interaction,
      filterType,
    );
  };

  const filterType: string = "Show Custom Quiz";

  return {
    View: view,
    NodeView: (nodeId: string, interaction: Interaction) => (
      <ShowCustomQuizNodePreview nodeId={nodeId} interaction={interaction} />
    ),
    FilterType: filterType,
    Create: create,
    Migrate: migrate,
    Copy: copy,
    Validate: validate,
    ValidateErrors: validateErrors,
    Filter: filter,
  };
};
